import { Money } from '@klear/money'
import { Timestamp } from '@klear/time'
import { KlearDate } from '@klear/date'
import type { KlearAccountTransactionBalanceInput } from '@klear/treasury/types'

function balanceInputInstant(value: Timestamp | number): Timestamp {
  return value instanceof Timestamp ? value : Timestamp.fromEpochMs(value)
}

function balanceInputCreatedAt(tx: KlearAccountTransactionBalanceInput): Timestamp {
  return balanceInputInstant(tx.createdAt)
}

export function balanceInputPostedAt(tx: KlearAccountTransactionBalanceInput): Timestamp {
  if (tx.postedAt != null) {
    return balanceInputInstant(tx.postedAt)
  }
  return balanceInputCreatedAt(tx)
}

export function balanceInputAvailableCashAt(tx: KlearAccountTransactionBalanceInput): Timestamp {
  if (tx.isOutbound) {
    return balanceInputCreatedAt(tx)
  }
  return balanceInputPostedAt(tx)
}

export type KlearAccountDailyBalanceDateMode = 'available-cash' | 'posted'

function balanceInputAtForMode(
  tx: KlearAccountTransactionBalanceInput,
  mode: KlearAccountDailyBalanceDateMode
): Timestamp {
  return mode === 'available-cash' ? balanceInputAvailableCashAt(tx) : balanceInputPostedAt(tx)
}

function balanceInputYmdForMode(
  tx: KlearAccountTransactionBalanceInput,
  mode: KlearAccountDailyBalanceDateMode
): string {
  return balanceInputAtForMode(tx, mode).toKlearDate().toJSON()
}

export function calculateKlearAccountDailyBalance(
  transactions: KlearAccountTransactionBalanceInput[] | undefined,
  currentBalance: Money,
  windowDays: number = 30,
  dateMode: KlearAccountDailyBalanceDateMode = 'available-cash'
): { date: string; balance: Money }[] {
  if (!transactions) {
    return []
  }
  const pendingOutbound = transactions.reduce((total, tx) => {
    if (!tx.isSettled && !tx.isFailed && tx.isOutbound) {
      return total.subtract(tx.amount)
    }
    return total
  }, currentBalance.zero)

  const settledBalance = currentBalance.add(pendingOutbound)

  const allTransactions = [...transactions]
  allTransactions.sort((a, b) =>
    balanceInputAtForMode(a, dateMode).isAfter(balanceInputAtForMode(b, dateMode)) ? -1 : 1
  )

  const transactionsByDate: Record<string, KlearAccountTransactionBalanceInput[]> = {}
  for (const tx of allTransactions) {
    if (!tx.isSettled) {
      continue
    }
    const dateStr = balanceInputYmdForMode(tx, dateMode)
    if (!transactionsByDate[dateStr]) {
      transactionsByDate[dateStr] = []
    }
    transactionsByDate[dateStr].push(tx)
  }

  const dailyBalances: { date: string; balance: Money }[] = []
  let runningBalance = settledBalance
  const endDate = KlearDate.today()
  let startDate = endDate.subtract(windowDays - 1, 'day')
  if (windowDays <= 0) {
    const oldestTx = allTransactions[allTransactions.length - 1]
    const daysSinceFirstTransaction = endDate.diff(
      oldestTx ? (KlearDate.parse(balanceInputYmdForMode(oldestTx, dateMode)) ?? KlearDate.today()) : KlearDate.today(),
      'day'
    )
    startDate = endDate.subtract(daysSinceFirstTransaction, 'day')
  }
  const todayStr = endDate.toJSON()

  for (
    let currentDate = endDate;
    currentDate.isAfter(startDate, 'day') || currentDate.isSame(startDate, 'day');
    currentDate = currentDate.subtract(1, 'day')
  ) {
    const dateStr = currentDate.toJSON()
    const todaysTxs = transactionsByDate[dateStr] || []

    const balanceToShow = dateStr === todayStr ? currentBalance : runningBalance
    dailyBalances.push({ date: dateStr, balance: balanceToShow })

    for (const tx of todaysTxs) {
      runningBalance = runningBalance.subtract(tx.amount)
    }
  }

  return dailyBalances.reverse()
}

export type KlearAccountTransactionRunningBalance<
  T extends KlearAccountTransactionBalanceInput = KlearAccountTransactionBalanceInput
> = {
  transaction: T
  balanceAfter: Money
}

export function calculateKlearAccountTransactionRunningBalances<T extends KlearAccountTransactionBalanceInput>(
  transactions: T[],
  currentBalance: Money
): KlearAccountTransactionRunningBalance<T>[] {
  const pendingOutbound = transactions.reduce((total, tx) => {
    if (!tx.isSettled && tx.isOutbound) {
      return total.subtract(tx.amount)
    }
    return total
  }, currentBalance.zero)

  let runningBalance = currentBalance.add(pendingOutbound)
  const settled = transactions
    .filter((tx) => tx.isSettled)
    .sort((a, b) => (balanceInputPostedAt(a).isAfter(balanceInputPostedAt(b)) ? -1 : 1))

  const results: KlearAccountTransactionRunningBalance<T>[] = []
  for (const tx of settled) {
    results.push({ transaction: tx, balanceAfter: runningBalance })
    runningBalance = runningBalance.subtract(tx.amount)
  }
  return results
}
